import React, { useRef, useState, useEffect } from 'react';
import { FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import './TestimonialsSection.css';

const TestimonialsSection = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

const testimonials = [
  {
    id: 1,
    name: "James H.",
    role: "Plumbing Business Owner",
    quote: "Build Start has been a game changer. We used to rely on word of mouth, but now 40% of our jobs come directly from the app. The verified badge definitely helps win trust.",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  {
    id: 2,
    name: "Sarah Jenkins",
    role: "Homeowner",
    quote: "I was terrified of hiring a 'cowboy' builder for my extension. Using Build Start allowed me to check credentials instantly. Found a fantastic team in 24 hours.",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg"
  },
  {
    id: 3,
    name: "Michael Chimes",
    role: "Property Developer",
    quote: "As a developer, I need reliability. I use the platform to source electricians and surveyors for multiple sites. It cuts my admin time in half.",
    avatar: "https://randomuser.me/api/portraits/men/22.jpg"
  },
  {
    id: 4,
    name: "Emma W.",
    role: "Homeowner",
    quote: "Simple, fast, and transparent. I posted a job for a bathroom reno and got three quotes from verified pros within the same day. Highly recommended.",
    avatar: "https://randomuser.me/api/portraits/women/65.jpg"
  },
  {
    id: 5,
    name: "David Ross",
    role: "Electrician",
    quote: "The lead quality is much higher here than on other directories. I'm not fighting with 20 other people for a tyre-kicker. The clients here are ready to hire.",
    avatar: "https://randomuser.me/api/portraits/men/86.jpg"
  },
  {
    id: 6,
    name: "Alan Thompson",
    role: "Quantity Surveyor",
    quote: "Precision is everything in my line of work. Build Start allows me to cross-reference local rates and find verified contractors for accurate cost estimation.",
    avatar: "https://randomuser.me/api/portraits/men/11.jpg" // Looks professional/suited
  },
  {
    id: 7,
    name: "Rebecca Miller",
    role: "Private Certifier",
    quote: "Compliance delays are a nightmare. I use this platform to recommend pre-vetted professionals to my clients who need to rectify defects quickly.",
    avatar: "https://randomuser.me/api/portraits/women/68.jpg" // Mature professional look
  },
  {
    id: 8,
    name: "Sophie K.",
    role: "Homeowner",
    quote: "We needed a structural engineer for a wall removal. I was dreading the ring-around. Build Start connected me with a local firm who sorted the calculations within a week.",
    avatar: "https://randomuser.me/api/portraits/women/33.jpg"
  },
  {
    id: 9,
    name: "Marcus Lee",
    role: "Conveyancer",
    quote: "When my clients buy a fixer-upper, they ask who to trust. The transparent review history here protects my clients from purchasing money pits.",
    avatar: "https://randomuser.me/api/portraits/men/54.jpg"
  },
  {
    id: 10,
    name: "Tom Barker",
    role: "Property Investor",
    quote: "Managing three rental properties used to be a headache. Now, when a tenant reports an issue, I source the professional through the app. Clean invoicing.",
    avatar: "https://randomuser.me/api/portraits/men/91.jpg" // Older/Experienced look
  }
];

  // Check scroll position to show/hide buttons
  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // Allow a small buffer (1px) for calculation errors
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Initial check and Event Listener
  useEffect(() => {
    checkScrollButtons();
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollButtons);
      }
    };
  }, []);

  // Scroll Handler
  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      // Scroll by card width (350px) + gap (30px)
      const scrollAmount = 380; 
      
      current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="bs-testimonials-section">
      <div className="bs-container">
        
        <div className="bs-testimonials-header">
           <div className="bs-header-stars">
              <FaStar /><FaStar /><FaStar /><FaStar /><FaStar /> 
              <span>4.9 Rated, 230+ Reviews</span>
           </div>
          <h2>HEAR WHAT THOUSANDS<br />OF OUR USERS THINK ABOUT US</h2>
          <p className="bs-testimonials-subtitle">
            Read about experiences of our users and see how we can connect you with the TOP Professional in Town.
          </p>
        </div>

        {/* Carousel Wrapper with relative positioning for arrows */}
        <div className="bs-carousel-wrapper">
          
          {/* Left Button */}
          <button 
            className={`bs-nav-btn bs-left-btn ${!canScrollLeft ? 'hidden' : ''}`} 
            onClick={() => scroll('left')}
            aria-label="Scroll Left"
          >
            <FaChevronLeft />
          </button>

          {/* Horizontal Scroll Container */}
          <div className="bs-testimonials-scroller" ref={scrollRef}>
             {testimonials.map((t) => (
               <div key={t.id} className="bs-testimonial-card">
                 <div className="bs-card-top">
                   <FcGoogle size={24} />
                   <div className="bs-card-stars">
                      <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                   </div>
                 </div>
                 
                 <p className="bs-quote">"{t.quote}"</p>
                 
                 <div className="bs-author">
                   <img src={t.avatar} alt={t.name} className="bs-avatar" />
                   <div className="bs-author-info">
                     <strong>{t.name}</strong>
                     <span>{t.role}</span>
                   </div>
                 </div>
               </div>
             ))}
          </div>

          {/* Right Button */}
          <button 
            className={`bs-nav-btn bs-right-btn ${!canScrollRight ? 'hidden' : ''}`}
            onClick={() => scroll('right')}
            aria-label="Scroll Right"
          >
            <FaChevronRight />
          </button>

        </div>

        {/* Visual Dots */}
        <div className="bs-scroller-dots">
            {/* Note: Making dots fully functional requires more complex IntersectionObserver logic. 
                Static dots are often acceptable if arrows are the primary nav. */}
            <span></span><span className="active"></span><span></span>
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;